export const UPLOAD_STATUS = {
    UPLOADING: 'uploading',
    DONE: 'done',
    ERROR: 'error'
};

// How long a finished or failed upload is held on screen before it starts to
// leave. It is held even when the transfer took no time at all, so a small file
// still reports itself instead of blinking past.
const DISMISS_DELAY = 2000;

// Has to match the fade of .upload-progress__item--leaving, which runs for the
// @uispeed of the design system.
const FADE_DURATION = 250;

/**
 * Holds the uploads that are currently on screen.
 *
 * Any feature that sends a file reports it here and the <upload-progress>
 * component mounted in the application layout renders it, so a new upload does
 * not have to bring its own progress UI along. The caller owns the transfer
 * itself - this only keeps what is shown about it.
 */
export default class {
    constructor ($timeout, $rootScope) {
        this.$timeout = $timeout;
        this.$rootScope = $rootScope;
        this.uploads = [];
        this.lastId = 0;
    }

    /**
     * Transfer events do not arrive inside a digest, so every change is flushed
     * by hand. $applyAsync only schedules a flush, which makes it safe to call
     * from callers that are already inside one.
     */
    apply () {
        this.$rootScope.$applyAsync();
    }

    /**
     * Puts a file on screen and returns the handle to report it with. Pass the
     * size when it is known, otherwise the item shows as still working until it
     * is finished or fails.
     */
    start (name, size) {
        const upload = {
            id: ++this.lastId,
            name,
            size: size || 0,
            loaded: 0,
            progress: 0,
            status: UPLOAD_STATUS.UPLOADING,
            error: null,
            isLeaving: false
        };

        this.uploads.push(upload);
        this.apply();

        return upload;
    }

    /**
     * Reports the bytes transferred so far. The bar is held below 100% while the
     * request is still open, so a full bar only ever means a finished upload.
     */
    setLoaded (upload, loaded) {
        if (!upload || upload.status !== UPLOAD_STATUS.UPLOADING) {
            return;
        }

        upload.loaded = upload.size ? Math.min(loaded, upload.size) : loaded;
        upload.progress = upload.size
            ? Math.min(99, Math.floor(100 * upload.loaded / upload.size))
            : 0;
        this.apply();
    }

    succeed (upload) {
        if (!upload) {
            return;
        }

        upload.status = UPLOAD_STATUS.DONE;
        upload.progress = 100;
        upload.loaded = upload.size;

        this.apply();
        this.$timeout(() => this.fadeOut(upload), DISMISS_DELAY);
    }

    /**
     * Leaving is two steps: the card is marked so it can play its fade, and only
     * then is it dropped. Removing it outright would make it vanish mid-animation.
     */
    fadeOut (upload) {
        if (this.uploads.indexOf(upload) === -1) {
            return;
        }

        upload.isLeaving = true;
        this.apply();

        this.$timeout(() => this.dismiss(upload), FADE_DURATION);
    }

    fail (upload, error) {
        if (!upload) {
            return;
        }

        upload.status = UPLOAD_STATUS.ERROR;
        upload.error = error || null;

        this.apply();
        this.$timeout(() => this.fadeOut(upload), DISMISS_DELAY);
    }

    /**
     * Shows a file that was turned down before it was sent, e.g. one over the size limit.
     */
    reject (name, error) {
        this.fail(this.start(name), error);
    }

    dismiss (upload) {
        const index = this.uploads.indexOf(upload);

        if (index > -1) {
            this.uploads.splice(index, 1);
            this.apply();
        }
    }

    isUploading () {
        return this.uploads.some(upload => upload.status === UPLOAD_STATUS.UPLOADING);
    }
}



// WEBPACK FOOTER //
// ./src/app/services/uploadProgress/index.js